import axios from 'axios';
import RoundHelper from '../utils/roundHelper';

const API_BASE_URL = process.env.VUE_APP_API_URL;

// Configuração global do axios
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  }
});

// Interceptor para adicionar token automaticamente
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para tratar erros globalmente
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      switch (error.response.status) {
        case 401:
          console.error('Não autorizado - Token inválido ou expirado');
          localStorage.removeItem('token');
          // Redirecionar para login será feito pelo App.vue
          break;
        case 403:
          console.error('Acesso negado - Sem permissão');
          break;
        case 404:
          console.error('Recurso não encontrado');
          break;
        case 500:
          console.error('Erro interno do servidor');
          break;
        default:
          console.error('Erro na requisição:', error.message);
      }
    } else if (error.request) {
      console.error('Servidor não respondeu:', error.message);
    } else {
      console.error('Erro ao configurar requisição:', error.message);
    }
    return Promise.reject(error);
  }
);

/**
 * Serviço de API para gerenciar requisições relacionadas a palpites e rodadas
 */
class ApiService {

  /**
   * Realiza login do usuário
   * @param {Object} user - Dados do usuário (email, token Firebase)
   * @returns {Promise<Object>} Token JWT e dados do usuário
   */
  static async login(user) {
    try {
      const response = await apiClient.post('/auth/login', user);
      
      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
      }
      
      return response.data;
    } catch (error) {
      localStorage.removeItem('token');
      throw error;
    }
  }
  /**
   * Busca a rodada atual do campeonato
   * @returns {Promise<Object>} Dados da rodada atual com jogos
   */
  static async getCurrentRound() {
    try {
      const response = await apiClient.get('/round/current', {
        headers: { permission: ['betting'] }
      });
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar rodada atual:', error);
      throw error;
    }
  }

  /**
   * Cria um novo palpite para o usuário
   * @param {number} userId - ID do usuário
   * @param {Object} bet - Dados do palpite (roundId, bets[])
   * @returns {Promise<Object>} Palpite criado
   */
  static async createBetting(userId, bet) {
    try {
      const response = await apiClient.post(`/users/${userId}/bets`, bet, {
        headers: { permission: ['betting', 'super-admin'] }
      });
      
      if (![200, 201].includes(response.status)) {
        throw new Error('Falha ao criar palpite');
      }
      
      return response.data;
    } catch (error) {
      console.error('Erro ao criar palpite:', error);
      throw error;
    }
  }

  /**
   * Atualiza um palpite existente
   * @param {number} userId - ID do usuário
   * @param {Object} bet - Dados do palpite com ID
   * @returns {Promise<Object>} Palpite atualizado
   */
  static async updateBetting(userId, bet) {
    try {
      const response = await apiClient.put(
        `/users/${userId}/bets/${bet._id}`, 
        bet, 
        {
          headers: { permission: ['betting-update'] }
        }
      );
      
      if (![200, 201, 204].includes(response.status)) {
        throw new Error('Falha ao atualizar palpite');
      }
      
      return response.data;
    } catch (error) {
      console.error('Erro ao atualizar palpite:', error);
      throw error;
    }
  }

  /**
   * Busca o palpite do usuário para uma rodada específica
   * @param {number} userId - ID do usuário
   * @param {number} roundId - ID da rodada
   * @returns {Promise<Object>} Palpite do usuário
   */
  static async getUserBet(userId, roundId) {
    try {
      const response = await apiClient.get(`/users/${userId}/bets/${roundId}`, {
        headers: { permission: ['betting'] }
      });
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar palpite do usuário:', error);
      throw error;
    }
  }

  /**
   * Verifica se a rodada já foi iniciada (primeiro jogo começou)
   * @param {Array} fixtures - Lista de jogos da rodada
   * @returns {boolean} True se rodada iniciada, false caso contrário
   */
  static roundInitiaded(fixtures) {
    if (!fixtures || fixtures.length === 0) {
      return false;
    }

    const firstMatch = RoundHelper.getFirstMatch(fixtures);
    
    if (!firstMatch || !firstMatch.eventTimestamp) {
      return false;
    }

    const eventTimeStamp = this.convertTimestampToGmtBr(
      firstMatch.eventTimestamp * 1000
    );

    const currentTimestamp = this.convertTimestampToGmtBr(
      Date.now()
    );

    return eventTimeStamp.getTime() <= currentTimestamp.getTime();
  }

  /**
   * Converte timestamp para horário de Brasília (GMT-3)
   * @param {number} timestamp - Timestamp em milissegundos
   * @returns {Date} Data convertida para GMT-3
   */
  static convertTimestampToGmtBr(timestamp) {
    const date = new Date(timestamp);
    const dateString = date.toLocaleString('en-US', { 
      timeZone: 'America/Sao_Paulo' 
    });
    return new Date(dateString);
  }

  /**
   * Busca rodada consolidada com palpites do usuário
   * @param {number} userId - ID do usuário
   * @param {number} roundId - ID da rodada
   * @returns {Promise<Object>} Rodada com jogos e palpites
   */
  static async getCurrentRoundWithBets(userId, roundId) {
    try {
      const response = await apiClient.get(
        `/users/${userId}/bets/${roundId}/consolidate`,
        {
          headers: { permission: ['betting'] }
        }
      );
      
      if (![200, 201].includes(response.status)) {
        throw new Error('Falha ao buscar rodada consolidada');
      }
      
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar rodada consolidada:', error);
      throw error;
    }
  }

  /**
   * Busca lista de IDs de todas as rodadas
   * @returns {Promise<Array>} Array com IDs das rodadas
   */
  static async getRoundIds() {
    try {
      const response = await apiClient.get('/round_ids', {
        headers: { permission: ['betting'] }
      });
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar IDs das rodadas:', error);
      throw error;
    }
  }

  /**
   * Busca rodada específica por ID
   * @param {number} roundId - ID da rodada
   * @returns {Promise<Object>} Dados da rodada
   */
  static async getRoundById(roundId) {
    try {
      const response = await apiClient.get(`/round/${roundId}`, {
        headers: { permission: ['betting'] }
      });
      return response.data;
    } catch (error) {
      console.error(`Erro ao buscar rodada ${roundId}:`, error);
      throw error;
    }
  }

  /**
   * Deleta um palpite
   * @param {number} userId - ID do usuário
   * @param {string} betId - ID do palpite
   * @returns {Promise<void>}
   */
  static async deleteBetting(userId, betId) {
    try {
      await apiClient.delete(`/users/${userId}/bets/${betId}`, {
        headers: { permission: ['betting', 'super-admin'] }
      });
    } catch (error) {
      console.error('Erro ao deletar palpite:', error);
      throw error;
    }
  }

  /**
   * Valida se um palpite pode ser editado
   * @param {Object} bet - Dados do palpite
   * @param {Array} fixtures - Jogos da rodada
   * @returns {Object} { canEdit: boolean, message: string }
   */
  static validateBettingEdit(bet, fixtures) {
    if (!bet || !fixtures) {
      return { 
        canEdit: false, 
        message: 'Dados inválidos' 
      };
    }

    const roundStarted = this.roundInitiaded(fixtures);
    
    if (roundStarted) {
      return { 
        canEdit: false, 
        message: 'A rodada já foi iniciada. Não é possível editar palpites.' 
      };
    }

    return { 
      canEdit: true, 
      message: 'Palpite pode ser editado' 
    };
  }

  /**
   * Formata dados do palpite para envio
   * @param {number} roundId - ID da rodada
   * @param {Array} bets - Array de palpites { matchId, homeScore, awayScore }
   * @returns {Object} Dados formatados
   */
  static formatBettingData(roundId, bets) {
    return {
      roundId,
      bets: bets.map(bet => ({
        matchId: bet.matchId,
        homeScore: parseInt(bet.homeScore, 10) || 0,
        awayScore: parseInt(bet.awayScore, 10) || 0
      }))
    };
  }
}

export default ApiService;